<template>
  <mailbox-settings-collapsible-card title="Mailbox Priority" class="mailbox-settings-routing">
    <p>
      {{ email }} is shared by {{ orderedMailboxes.length }} mailboxes. Each new email goes to the first mailbox
      on this list whose filters accept it, and replies go to their conversation's mailbox. Drag the mailboxes to change their order.
    </p>
    <draggable
      :animation="180"
      :scroll-sensitivity="100"
      :scroll-speed="20"
      :force-fallback="true"
      :list="orderedMailboxes"
      :handle="'.drag-handle'"
      :disabled="isSubmitting"
      drag-class="drag-card"
      item-key="mailboxID"
      @end="saveSortOrder"
    >
      <template #item="{ element: mailbox, index }">
        <div class="mailbox-settings-routing__row">
          <span class="drag-handle" title="Drag to reorder" @mousedown.prevent><i class="ddi ddi-sort" /></span>
          <span class="mailbox-settings-routing__rank">{{ index + 1 }}</span>
          <div class="mailbox-settings-routing__details">
            <div class="mailbox-settings-routing__name">
              <span>{{ mailbox.name }}</span>
              <span v-if="mailbox.mailboxID === id" class="mailbox-settings-routing__tag mailbox-settings-routing__tag--current">This mailbox</span>
              <span v-if="isCatchAll(mailbox)" class="mailbox-settings-routing__tag mailbox-settings-routing__tag--catch-all">Catch-all</span>
            </div>
            <span class="mailbox-settings-routing__meta">{{ summarize(mailbox) }}</span>
            <span v-if="notes[index]" class="mailbox-settings-routing__meta">{{ notes[index] }}</span>
          </div>
          <router-link
            v-if="mailbox.canAdmin && mailbox.mailboxID !== id"
            :to="RouteAssistant.getMailboxSettingsDirectConnectionRoute(accountID, mailbox.mailboxID)"
            class="just-a-link mailbox-settings-routing__edit"
          >Edit filters</router-link>
        </div>
      </template>
    </draggable>
    <p v-if="!hasCatchAll" class="mailbox-settings-routing__footer">
      Emails that none of these mailboxes accept are ignored.
    </p>
  </mailbox-settings-collapsible-card>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Components */
import draggable from 'vuedraggable'
import MailboxSettingsCollapsibleCard from '@/screens/mailbox-settings/components/mailbox-settings-collapsible-card.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import MailboxFilterAssistant from '@/helpers/mailbox-filter-assistant'

/* Data types */
import { IConnectionMailbox } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'MailboxSettingsDirectConnectionRouting',

  components: {
    draggable,
    MailboxSettingsCollapsibleCard,
  },

  props: {
    /* Mailbox ID */
    id: {
      type: Number,
      required: true,
    },
    /* The connection's email address */
    email: {
      type: String,
      required: true,
    },
    /* The mailboxes on the connection, highest priority first */
    mailboxes: {
      type: Array as () => IConnectionMailbox[],
      required: true,
    },
  },

  emits: ['sorted'],

  data() {
    return {
      RouteAssistant,
      isSubmitting: false,
      orderedMailboxes: [] as IConnectionMailbox[],
    }
  },

  computed: {
    accountID(): string {
      return this.$route.params.accountId
    },
    hasCatchAll(): boolean {
      return this.orderedMailboxes.some(m => this.isCatchAll(m))
    },
    /* A mailbox below a catch-all only gets the emails it blocks, and none when it blocks nothing */
    notes(): string[] {
      return this.orderedMailboxes.map((mailbox, index) => {
        const catchAll = this.orderedMailboxes
          .slice(0, index)
          .find(m => this.isCatchAll(m))

        if (!catchAll) {
          return ''
        }

        return catchAll.filters.length
          ? `Only gets new emails that ${catchAll.name} blocks.`
          : `Never gets new emails, because ${
              catchAll.name
            } accepts every email first.`
      })
    },
  },

  watch: {
    mailboxes: {
      deep: 1,
      immediate: true,
      handler(mailboxes: IConnectionMailbox[]) {
        this.orderedMailboxes = [...mailboxes]
      },
    },
  },

  methods: {
    isCatchAll(mailbox: IConnectionMailbox): boolean {
      return MailboxFilterAssistant.isCatchAll(mailbox.filters)
    },
    summarize(mailbox: IConnectionMailbox): string {
      return MailboxFilterAssistant.summarize(mailbox.filters)
    },
    async saveSortOrder(event: { oldIndex: number; newIndex: number }) {
      if (event.oldIndex === event.newIndex) {
        return
      }

      try {
        this.isSubmitting = true
        await MailboxesAPI.put_GrantMailboxesUpdateSort(
          this.id,
          this.orderedMailboxes.map(m => m.mailboxID),
        )
        this.$messaging.showSuccess('The mailbox priority has been updated!')
        this.$emit('sorted', [...this.orderedMailboxes])
      } catch (error) {
        this.$error.emit(error as any, true)
        this.orderedMailboxes = [...this.mailboxes]
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-routing {
  margin-bottom: 20px;

  &__row {
    display: flex;
    align-items: center;
    padding: 12px 0;
    background-color: var(--color-background-primary);

    &:not(:last-child) {
      border-bottom: $border--solid-extra-light;
    }

    .drag-handle {
      cursor: grab;
      color: var(--color-text-third);
      font-size: 16px;
      margin-right: 10px;
    }
  }

  &__rank {
    flex: 0 0 20px;
    color: var(--color-text-third);
    font-weight: $bold;
  }

  &__details {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    @include lettering--p;
  }

  &__tag {
    margin-left: 8px;
    padding: 0 6px;
    border: $border--solid-extra-light;
    border-radius: 4px;
    font-size: 11px;
    color: var(--color-text-third);

    &--current {
      font-weight: $bold;
      color: var(--color-text-primary);
      border-color: var(--color-text-primary);
    }

    &--catch-all {
      font-weight: $bold;
      color: $color--green;
      border-color: $color--green;
    }
  }

  &__meta {
    font-size: 12px;
    color: var(--color-text-third);
  }

  &__edit {
    margin-left: auto;
    padding-left: 12px;
    font-size: 12px;
    white-space: nowrap;
  }

  &__footer {
    margin: 16px 0 0;
    color: var(--color-text-third);
  }
}

.drag-card {
  cursor: grab !important;
  opacity: 0.95 !important;
  background-color: var(--color-background-primary) !important;
  border: solid 1px $light-blue-grey-two;
}
</style>
